import React, { useState, useEffect, useRef } from "react";
import { FaSearch, FaEye, FaArrowLeft, FaCalendarAlt } from "react-icons/fa";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
// Reuse the Review Existing Goals page styles verbatim so this page matches it
// pixel for pixel; ClientHistory.css only adds the review-history extras.
import "./GoalsPage.css";
import "./ClientHistory.css";
// Single shared implementation so this table's GOAL DOMAIN column can never
// drift from the Review Existing Goals table. Imported from the module that
// owns it rather than re-exported through a page component.
import { buildDomainTags, buildDomainTagsFromGoal } from "./goalDomainTags";
import { apiUrl } from "../apiConfig";

const API_BASE = `${apiUrl("therapist_api")}/`;

const formatDate = (value) => {
  if (!value) return "";
  const datePart = String(value).split(" ")[0];
  const parts = datePart.split("-");
  if (parts.length !== 3) return value;
  const [year, month, day] = parts;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (year && month && day) return `${parseInt(day)} ${months[parseInt(month) - 1]} ${year}`;
  return value;
};

const sessionLabel = (appointmentType) => {
  const value = String(appointmentType || "");
  if (!value) return "";
  // Canonical labels used across the app - the API returns the raw pathway
  // label ("review smart goal"), which would otherwise read mangled here.
  const map = {
    initial: "Initial",
    review: "Review",
    "review smart goal": "Review Smart Goal",
  };
  const key = value.trim().toLowerCase();
  return map[key] || value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

// Goal domains arrive as two index-aligned arrays (goalDomain / goalSkills) and
// are rendered by the shared tag builder, so a cell reads
// "Motor - Grasp And Release" rather than the bare domain. That builder also
// collapses a domain repeated as its own skill, so "Vision" never shows as
// "Vision-Vision".

/**
 * Flatten sessions x reviews into one table row per SAVED STATE.
 *
 * This is what makes the page an audit trail:
 *   - the original Intervention Planning goal ("Goal Set", reviewId 0)
 *   - one row per Goal Review, newest last
 *
 * The page previously rendered `json.sessions` directly - ONE row per session,
 * using only the session's latest values - so `session.reviews` was fetched but
 * never displayed. The result was that repeated Edit / Update presses on
 * /goalreview changed the single row in place and no new history row ever
 * appeared, which is exactly the opposite of the required behaviour.
 *
 * The session's own values are used for the base row only; every review row
 * takes its SMART GOAL / TARGET / STATUS / DOMAIN from that specific review, so
 * a row is a faithful snapshot of what was saved at that moment.
 */
const buildHistoryRows = (sessions) => {
  const out = [];

  (Array.isArray(sessions) ? sessions : []).forEach((s) => {
    if (!s) return;

    const base = {
      client: s.client,
      clientId: s.clientId,
      clientRef: s.clientRef,
      clientCode: s.clientCode,
      appointmentType: s.appointmentType,
      ageGroup: s.ageGroup,
      sessionId: s.sessionId,
      isDraft: s.isDraft,
      summaryConfirmed: s.summaryConfirmed,
      answerCount: s.answerCount,
      // Every row this builder emits exists only because goals were saved, so
      // the "view goals" eye is always enabled here. Carried explicitly because
      // the session-level flag used to come straight off the API payload.
      hasGoals: true,
    };

    // --- Row 1: the original Goal Set (reviewId 0) -------------------------
    // Only shown when the session actually has planning goals, so a session that
    // was never planned does not produce a misleading empty row.
    if (s.hasGoals) {
      out.push({
        ...base,
        rowKey: `${s.sessionId}-base`,
        isBaseRow: true,
        reviewId: 0,
        reviewNo: 0,
        rowLabel: "Goal Set",
        assessmentDate: s.assessmentDate,
        lastReviewDate: s.lastReviewDate,
        smartGoal: s.smartGoal,
        goalDomainTags: buildDomainTags({
          goalDomain: s.goalDomain,
          goalSkills: s.goalSkills,
        }),
        targetDate: s.targetDate,
        status: s.status,
        achievedBy: s.achievedBy,
        isClosed: s.isClosed,
        comments: s.comments,
      });
    }

    // --- One row per saved Goal Review ------------------------------------
    const reviews = Array.isArray(s.reviews) ? s.reviews : [];
    reviews.forEach((rv) => {
      const goals = Array.isArray(rv.goals) ? rv.goals : [];
      const g = goals[0] || {};
      out.push({
        ...base,
        rowKey: `${s.sessionId}-r${rv.reviewId}`,
        isBaseRow: false,
        reviewId: rv.reviewId,
        reviewNo: rv.reviewNo,
        rowLabel: `Review ${rv.reviewNo}`,
        // The review is what dated this state, so it is the ASSESSED date for
        // that row; fall back to the session date if it is somehow missing.
        assessmentDate: rv.reviewCreatedAt || s.assessmentDate,
        lastReviewDate: rv.reviewCreatedAt || s.lastReviewDate,
        smartGoal: g.smart_goal ?? "",
        goalDomainTags: buildDomainTagsFromGoal(g),
        targetDate: g.target_date || "",
        status: g.status === "Select" ? "" : g.status || "",
        achievedBy: g.result_date || "",
        isClosed: Number(g.is_closed) === 1 ? 1 : 0,
        comments: g.outcome_measure || "",
      });
    });
  });

  // Newest first, matching the Review Existing Goals page.
  return out.sort((a, b) => new Date(b.assessmentDate || 0) - new Date(a.assessmentDate || 0));
};

const ClientHistory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const clientId = searchParams.get("clientId") || "";
  const focusSessionId = searchParams.get("sessionId") || "";

  const [client, setClient] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Same filter / sort / pagination behaviour as the Review Existing Goals page.
  const [globalSearch, setGlobalSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  // Every saved state is its own table row now (the original Goal Set, then
  // one row per Review), so there is no separate review-history accordion to
  // expand any more - the rows themselves are the history.

  const token = localStorage.getItem("token");

  const loadHistory = async () => {
    if (!clientId) {
      setLoading(false);
      setError("No client was selected.");
      return;
    }
    if (!token) {
      setLoading(false);
      setError("Session expired. Please sign in again.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const response = await fetch(`${API_BASE}get_client_history.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(clientId) }),
      });
      const json = await response.json();
      if (json.status) {
        setClient(json.client || null);
        setData(buildHistoryRows(json.sessions));
      } else {
        setData([]);
        setError(json.message || "Unable to load the client history.");
      }
    } catch (err) {
      setData([]);
      setError(err.message || "Network error while loading the client history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, token]);

  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.key === column && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: column, direction });
  };

  useEffect(() => { setCurrentPage(1); }, [globalSearch, fromDate, toDate]);

  // Filtering - identical semantics to the Review Existing Goals page.
  let filteredData = data.filter((row) => {
    const q = globalSearch.toLowerCase();
    const matchesGlobal =
      String(row.clientId ?? "").toLowerCase().includes(q) ||
      String(row.client ?? "").toLowerCase().includes(q) ||
      String(row.assessmentDate ?? "").toLowerCase().includes(q) ||
      String(row.appointmentType ?? "").toLowerCase().includes(q) ||
      String(row.rowLabel ?? "").toLowerCase().includes(q) ||
      String(row.smartGoal ?? "").toLowerCase().includes(q) ||
      // Match the rendered "Domain - Skill" tags, so searching for either half
      // finds the row - searching the skill alone used to return nothing.
      (row.goalDomainTags || []).join(" ").toLowerCase().includes(q) ||
      String(row.targetDate ?? "").toLowerCase().includes(q) ||
      String(row.lastReviewDate ?? "").toLowerCase().includes(q) ||
      String(row.status ?? "").toLowerCase().includes(q);

    let rowDate = null;
    if (row.assessmentDate) {
      rowDate = new Date(String(row.assessmentDate).split(" ")[0]);
    }

    const from = fromDate ? new Date(fromDate) : new Date("1900-01-01");
    const to = toDate ? new Date(toDate) : new Date("2100-12-31");
    if (toDate) to.setHours(23, 59, 59, 999);

    const matchesDateRange =
      rowDate && !isNaN(rowDate.getTime())
        ? rowDate >= from && rowDate <= to
        : true;

    return matchesGlobal && matchesDateRange;
  });

  if (sortConfig.key) {
    filteredData = [...filteredData].sort((a, b) => {
      // GOAL DOMAIN lives in an array of rendered tags, so read the sort value
      // from there; every other column is a plain scalar.
      const read = (row) => {
        const v = row[sortConfig.key];
        if (Array.isArray(v)) return v.join(", ");
        return v == null ? "" : v;
      };
      const x = read(a);
      const y = read(b);
      if (x < y) return sortConfig.direction === "asc" ? -1 : 1;
      if (x > y) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
  const paginatedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const goToPage = (page) => { if (page >= 1 && page <= totalPages) setCurrentPage(page); };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const subtitle = client
    ? `${client.clientName}${client.clientCode ? ` | ID: ${client.clientCode}` : ""}${client.ageGroup ? ` | Age group: ${client.ageGroup}` : ""}`
    : "Track caregiver led goals here";

  return (
    <>
      <Link onClick={() => navigate(-1)} className="back-btn2" aria-label="Go back to dashboard">
        <FaArrowLeft className="back-icon" aria-hidden="true" />
        Previous page
      </Link>
      <main className="continue-container">
        <div className="common-page-header">
          <h1 className="common-page-title">
            {client ? `${client.clientName} - Goal Review Records` : "Goal Review Records"}
          </h1>
          <p className="common-page-subtitle">{subtitle}</p>
        </div>

        <div className="filter-section">
          <div className="search-box">
            <div className="common-search-box">
              <input
                type="text"
                placeholder="Search"
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                aria-label="Search this client's history"
              />
              <FaSearch className="common-search-icon" />
            </div>
          </div>

          <div className="filter-date-range">
            <div className="filter-date-wrapper">
              <label htmlFor="history-from-date" className="sr-only">From Date</label>
              <div className="filter-date-box" role="button" tabIndex="0" onClick={() => fromDateRef.current?.showPicker()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fromDateRef.current?.showPicker(); } }} aria-label={`Filter from date${fromDate ? `: ${fromDate}` : ""}`} aria-haspopup="dialog">
                <span className={!fromDate ? "filter-date-placeholder" : "filter-date-value"}>
                  {fromDate || "From"}
                </span>
                <FaCalendarAlt className="filter-date-icon" />
                <input
                  type="date"
                  id="history-from-date"
                  name="fromDate"
                  ref={fromDateRef}
                  value={fromDate}
                  onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }}
                  className="filter-date-input"
                  aria-label="From date"
                />
              </div>
            </div>

            <div className="filter-date-wrapper">
              <label htmlFor="history-to-date" className="sr-only">To Date</label>
              <div className="filter-date-box" role="button" tabIndex="0" onClick={() => toDateRef.current?.showPicker()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toDateRef.current?.showPicker(); } }} aria-label={`Filter to date${toDate ? `: ${toDate}` : ""}`} aria-haspopup="dialog">
                <span className={!toDate ? "filter-date-placeholder" : "filter-date-value"}>
                  {toDate || "To"}
                </span>
                <FaCalendarAlt className="filter-date-icon" />
                <input
                  type="date"
                  id="history-to-date"
                  name="toDate"
                  ref={toDateRef}
                  value={toDate}
                  onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }}
                  className="filter-date-input"
                  aria-label="To date"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="table-wrapper gotable-wrapper">
          {loading ? (
            <div style={{ textAlign: "center", padding: "20px" }} role="status" aria-live="polite">
              Loading client history...
            </div>
          ) : error ? (
            <div style={{ textAlign: "center", padding: "20px" }} role="alert">{error}</div>
          ) : (
            <table className="goals-table" style={{ marginBottom: "20px", tableLayout: "fixed" }}>
              <caption className="sr-only">Client history data</caption>
              <colgroup>
                <col style={{ width: "14%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "11%" }} />
              </colgroup>
              <thead>
                <tr>
                  {[
                    { key: "client", label: "CLIENT" },
                    { key: "assessmentDate", label: "ASSESSED" },
                    { key: "smartGoal", label: "SMART GOAL" },
                    { key: "goalDomainTags", label: "GOAL DOMAIN" },
                    { key: "appointmentType", label: "SESSION" },
                    { key: "targetDate", label: "TARGET" },
                    { key: "lastReviewDate", label: "LAST REVIEW" },
                    { key: "status", label: "STATUS" },
                  ].map(({ key, label }) => (
                    <th
                      key={key}
                      scope="col"
                      onClick={() => handleSort(key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSort(key);
                        }
                      }}
                      tabIndex="0"
                      aria-label={`Sort by ${label}${sortConfig?.key === key ? `, current order ${sortConfig.direction === "asc" ? "ascending" : "descending"}` : ""}`}
                      aria-sort={sortConfig?.key === key ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"}
                    >
                      {label}
                    </th>
                  ))}
                  <th scope="col">ACTIONS</th>
                </tr>
              </thead>

              <tbody>
                {paginatedData.map((row, index) => (
                  <React.Fragment key={row.rowKey || row.sessionId}>
                    <tr
                      className={[
                        String(row.sessionId) === String(focusSessionId) ? "ch-row-focused" : "",
                        row.isBaseRow ? "ch-row-base" : "ch-row-review",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <td className="goals-td-client">
                        <span className="goals-client-name">{row.client}</span>
                        <span className="goals-client-id">{row.clientRef}</span>
                      </td>

                      <td className="goals-td-center">{formatDate(row.assessmentDate)}</td>

                      <td className="goals-td-left">{row.smartGoal}</td>

                      <td className="goals-td-domain">
                        {(row.goalDomainTags || []).map((tag, i) => (
                          <span key={i} className="goals-domain-tag">
                            {tag}
                          </span>
                        ))}
                      </td>

                      <td className="goals-td-center">{sessionLabel(row.appointmentType)}</td>

                      <td className="goals-td-center">{formatDate(row.targetDate)}</td>

                      <td className="goals-td-center">{formatDate(row.lastReviewDate)}</td>

                      <td className="goals-td-status">
                        {row.status
                          ? row.status.split("-").map((s, i) => (
                              <span key={i} className="goals-status-badge">
                                {s.trim()}
                              </span>
                            ))
                          : "In progress - on track"}
                      </td>

                      <td className="goals-td-actions">
                        <div className="goals-actions-row">
                          {/*
                            Review opens the read-only history page for THIS
                            row, not the editable review page. reviewId 0 is
                            the original Goal Set row; every other row carries
                            the id of the exact review version it came from, so
                            older rows still show the values they had.
                          */}
                          <Link
                            to={`/goalreview-history?sessionId=${row.sessionId}&reviewId=${row.isBaseRow ? 0 : row.reviewId}`}
                            className="goals-action-review"
                            aria-label={`View the goal review for ${row.client} - ${row.rowLabel || "this version"}`}
                          >
                            Review
                          </Link>

                          {row.hasGoals ? (
                            <Link
                              to={`/goalsetdownload?sessionId=${row.sessionId}`}
                              className="goals-action-icon"
                              aria-label={`View goals for ${row.client}`}
                            >
                              <FaEye className="goals-eye-icon" />
                            </Link>
                          ) : (
                            <span className="goals-action-icon goals-action-disabled">
                              <FaEye className="goals-eye-icon" />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}

          {!loading && !error && (
            <nav className="gopagination" aria-label="Pagination">
              <button disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)}>Previous</button>
              {getPageNumbers().map((p) => (
                <button
                  key={p}
                  className={p === currentPage ? 'active' : ''}
                  aria-current={p === currentPage ? "page" : undefined}
                  onClick={() => goToPage(p)}
                >
                  {p}
                </button>
              ))}
              <button disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)}>Next</button>
            </nav>
          )}
        </div>
      </main>
    </>
  );
};

export default ClientHistory;
